import apiClient, { fileUploadClient } from './api';
import type { TenantProfile, SubmitGuarantorRequest, KycStatus } from '../types/kyc';

export const tenantsApi = {
  // ---------------------------------------------------------------------------
  // Tenant self-service
  // ---------------------------------------------------------------------------

  createProfile: async (): Promise<TenantProfile> => {
    const res = await apiClient.post<TenantProfile>('/tenants/profile');
    return res.data;
  },

  getOwnProfile: async (): Promise<TenantProfile> => {
    const res = await apiClient.get<TenantProfile>('/tenants/profile');
    return res.data;
  },

  submitIdDocument: async (file: File, docType: string): Promise<TenantProfile> => {
    const form = new FormData();
    form.append('file', file);
    form.append('docType', docType);
    const res = await fileUploadClient.post<TenantProfile>('/tenants/profile/id-document', form);
    return res.data;
  },

  submitGuarantor: async (data: SubmitGuarantorRequest): Promise<TenantProfile> => {
    const res = await apiClient.post<TenantProfile>('/tenants/profile/guarantor', data);
    return res.data;
  },

  recordConsent: async (): Promise<TenantProfile> => {
    const res = await apiClient.post<TenantProfile>('/tenants/profile/consent');
    return res.data;
  },

  submitForReview: async (): Promise<TenantProfile> => {
    const res = await apiClient.post<TenantProfile>('/tenants/profile/submit');
    return res.data;
  },

  // ---------------------------------------------------------------------------
  // Manager / Admin
  // ---------------------------------------------------------------------------

  listProfiles: async (status?: KycStatus): Promise<TenantProfile[]> => {
    const params = status ? { status } : {};
    const res = await apiClient.get<TenantProfile[]>('/tenants', { params });
    return res.data;
  },

  getProfile: async (id: number): Promise<TenantProfile> => {
    const res = await apiClient.get<TenantProfile>(`/tenants/${id}`);
    return res.data;
  },

  approveKyc: async (id: number): Promise<TenantProfile> => {
    const res = await apiClient.patch<TenantProfile>(`/tenants/${id}/approve`);
    return res.data;
  },

  rejectKyc: async (id: number, reason: string): Promise<TenantProfile> => {
    const res = await apiClient.patch<TenantProfile>(`/tenants/${id}/reject`, { reason });
    return res.data;
  },

  // ---------------------------------------------------------------------------
  // Landlord KYC review
  // ---------------------------------------------------------------------------

  getLandlordPending: async () => {
    const res = await apiClient.get('/tenant-applications/landlord/pending');
    return res.data;
  },

  getLandlordHistory: async () => {
    const res = await apiClient.get('/tenant-applications/landlord/history');
    return res.data;
  },

  landlordApprove: async (applicationId: number): Promise<void> => {
    await apiClient.patch(`/tenant-applications/${applicationId}/landlord-decision`, {
      decision: 'APPROVED',
    });
  },

  landlordReject: async (applicationId: number, reason: string): Promise<void> => {
    await apiClient.patch(`/tenant-applications/${applicationId}/landlord-decision`, {
      decision: 'REJECTED',
      reason,
    });
  },

  getMyAssignment: async () => {
    const res = await apiClient.get('/tenants/my-assignment');
    return res.data as {
      source: 'lease' | 'application';
      building: { id: number; name: string; address: string; state: string } | null;
      unit: { id: number; unitNumber: string; type: string; floor: number | null } | null;
      property: { id: number; name: string; location: string; area: string; state: string } | null;
      landlordName: string | null;
    } | null;
  },
};
